import React, { useState } from 'react';
import { Building2, ChevronDown, KeyRound, LogOut, Menu } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../utils/api';
import { PartnerEndpoints } from '../../utils/endpoints';
import { Permissions, roleLabel } from '../../utils/permissions';
import useDropdown from '../../hooks/useDropdown';
import ChangePasswordModal from '../main-interface/modals/ChangePasswordModal';
import Avatar from './Avatar';

const Header = ({ onToggleCollapse, onOpenMobile }) => {
  const { authData, role, setAuthData, hasPermission, isSubMerchant, subMerchant, isEnterprise, enterprise } =
    useAuth();
  const { open, toggle, close, ref } = useDropdown();
  const navigate = useNavigate();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const accountName = () => {
    if (isEnterprise) return enterprise.name;
    if (isSubMerchant) return subMerchant.name;
    return authData.username;
  };
  const username = accountName() || 'Guest';
  const canSeeBusinessInfo = !isSubMerchant && !isEnterprise && hasPermission(Permissions.settingsAny);
  const canChangePassword = hasPermission(Permissions.passwordUpdate);

  const handleChangePassword = async ({ oldPassword, newPassword }) => {
    await api.put(PartnerEndpoints.changePassword, {
      old_password: oldPassword,
      new_password: newPassword,
    });
  };

  const openPasswordModal = () => {
    close();
    setPasswordModalOpen(true);
  };

  const handleLogout = () => {
    close();
    setAuthData({ username: '', token: '' });
    localStorage.removeItem('authData');
    sessionStorage.removeItem('authData');
    navigate('/', { replace: true });
  };

  return (
    <header className="pc-header">
      <div className="header-wrapper flex max-sm:px-[15px] px-[25px] grow">
        <div className="mr-auto">
          <ul className="inline-flex *:min-h-header-height *:inline-flex *:items-center">
            <li className="pc-h-item max-lg:hidden lg:inline-flex">
              <button
                type="button"
                className="pc-head-link !ml-0"
                aria-label="Toggle sidebar"
                onClick={onToggleCollapse}
              >
                <Menu size={20} />
              </button>
            </li>
            <li className="pc-h-item lg:hidden">
              <button
                type="button"
                className="pc-head-link !ml-0"
                aria-label="Open menu"
                onClick={onOpenMobile}
              >
                <Menu size={20} />
              </button>
            </li>
          </ul>
        </div>
        <div className="ml-auto">
          <ul className="inline-flex *:min-h-header-height *:inline-flex *:items-center">
            <li
              ref={ref}
              className={['dropdown', 'pc-h-item', open && 'drp-show'].filter(Boolean).join(' ')}
            >
              <button
                type="button"
                className="pc-head-link dropdown-toggle arrow-none !mr-0 w-auto px-3 gap-2"
                aria-expanded={open}
                onClick={toggle}
              >
                <Avatar name={username} />
                <span className="max-md:hidden font-medium">{username}</span>
                <ChevronDown size={14} />
              </button>
              <div className="dropdown-menu dropdown-menu-end dropdown-user-profile" hidden={!open}>
                <div className="flex items-center gap-3 px-[15px] py-2.5">
                  <Avatar name={username} size="lg" />
                  <div>
                    <h6 className="mb-0 font-medium text-theme-headings">{username}</h6>
                    <span className="text-sm text-theme-bodycolor">{roleLabel(role)}</span>
                  </div>
                </div>
                <hr className="border-theme-border" />
                {canSeeBusinessInfo && (
                  <Link to="/layout/business-info" className="dropdown-item" onClick={close}>
                    <Building2 />
                    <span>Business info</span>
                  </Link>
                )}
                {canChangePassword && (
                  <button type="button" className="dropdown-item" onClick={openPasswordModal}>
                    <KeyRound />
                    <span>Change password</span>
                  </button>
                )}
                {(canSeeBusinessInfo || canChangePassword) && <hr className="border-theme-border" />}
                <button type="button" className="dropdown-item" onClick={handleLogout}>
                  <LogOut />
                  <span>Log out</span>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSubmit={handleChangePassword}
      />
    </header>
  );
};

export default Header;
